/**
 * This is a collection of category objects representing the child categories of the current category.
 *
 * It must be instantiated with options.currentCat pointing to a Netx.Models.Category representing the current category.
 *
 * If options.cache is provided, it is assumed to be a Netx.Collections.CategoryTreeNodes collection,
 * and we fetch our categories through that object, using it as a read-thru cache.
 *
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/category';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

export default class Categories extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getCategories: 'Loading categories',
			getCategoryObjects: 'Loading categories',
			getDashboardRecentCategories: 'Loading dashboard categories',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getCategories: 'Loading categories',
			getCategoryObjects: 'Loaded categories',
			getDashboardRecentCategories: 'Loaded dashboard categories',
		};
	}

	/**
	 * @property {object} methods                                - Collection RPC methods
	 * @property {method} methods.read                           - Fetch categories
	 * @property {method} methods.getCategoryObjects             - Fetch categories for shallow models
	 * @property {method} methods._getCategoriesById             - Fetch categories by id
	 * @property {method} methods._getDashboardRecentCategories  - Fetch recent categories for dashboard
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				if (this.currentCat) {
					this.parentid = parseInt(this.currentCat.get('categoryid'));
				}

				if (!this.parentid) {
					this.parentid = 1;
				}

				// get the categories from the DAM
				if (!this.cache) {
					return ['getCategories', this.searchString, this.parentid];
				}

				// Get the categories from our cache
				// Really this cache mechanism should be deprecated; I thought I did that, but no:
				// Netx.category.list still caches off of Netx.category.tree .  So this is a stopgap 'till we fix all that.
				return this.cache.fetchChildren(this.parentid).then((response) => {
					return this.cache.where({
						parentid: this.parentid,
					});
				});
			},
			/**
			 * Just like users/groups
			 * @method
			 * @param {object} opts Request options
			 */
			getCategoryObjects(opts) {
				return ['getCategoryObjects', this.pluck('categoryid')];
			},
			/** @method */
			_getCategoriesById: ['getCategoryObjects', 'ids'],
			/** @method */
			_getDashboardRecentCategories: ['getDashboardRecentCategories'],
		};
	}

	/**
	 * @property {object}   parsers                                - Collection RPC methods
	 * @property {function} parsers.getCategoryObjects             - Parse fetch of categories for shallow models
	 * @property {function} parsers._getCategoriesById             - Parse fetch of categories by id
	 * @property {function} parsers._getDashboardRecentCategories  - Parse fetch of recent categories for dashboard
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} categories Categories
			 * @param {object} opts Request options
			 */
			getCategoryObjects(categories, opts) {
				this.set(categories, opts);
			},
			/**
			 * @function
			 * @param {array} categories Categories
			 * @param {object} opts Request options
			 */
			_getCategoriesById(categories, opts) {
				if (opts.asset) {
					categories = _.map(categories, (category) =>
						_.extend(category, {asset: opts.asset}),
					);
				}
				this.set(categories, opts);
			},
			/**
			 * @function
			 * @param {array} recentCategories Recent categories
			 * @param {object} opts Request options
			 */
			_getDashboardRecentCategories(recentCategories, opts) {
				this.set(recentCategories, opts);
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * A cache of categories we can fetch through, such as the category tree
		 * @type {Netx.Collections.Categories}
		 * @default
		 */
		this.cache = opts.cache;

		/**
		 * The parent category we should consider when fetching children
		 * @type {Netx.Models.Category}
		 * @default
		 */
		this.currentCat = opts.currentCat;

		/**
		 * Used by `getCategoriesById`
		 * @type {array}
		 * @default
		 * @todo This is is overriding our Netx.Collection.prototype.ids() - need to find all the places settign this and change to something else
		 */
		this.ids = opts.ids || [];

		/**
		 * @type {object}
		 * @default
		 * @todo are these used?
		 */
		this.nodes = _.extend(
			{
				active: undefined,
				activeParent: undefined,
				previous: undefined,
			},
			opts.nodes,
		);

		/**
		 * @type {number}
		 * @default
		 */
		this.parentid = opts.parentId;

		/**
		 * @type {string}
		 * @default
		 */
		this.searchString = opts.searchString || '';

		// We are the current category's children
		if (this.currentCat) {
			// React to category changes.
			this.listenTo(
				this.currentCat,
				'change:categoryid',
				(currentCat, newCatId, opts) => {
					this.fetch(opts);
				},
			);
		}
	}

	/**
	 * Fetch the children of any given category
	 * @method
	 * @param {number} parentId Parent category id
	 * @param {object} opts Request options
	 */
	fetchChildren(parentId, opts) {
		this.parentid = parentId;
		return this.fetch(opts);
	}

	/**
	 * @method
	 * @param {array} ids An array of ids
	 * @param {object} opts Request options
	 */
	getCategoriesById(ids, opts = {}) {
		this.ids = _.isArray(ids) ? ids : [parseInt(ids)];

		!opts.silent &&
			this.listenToOnce(
				this,
				'request:_getCategoriesById',
				(collection, xhr, opts) => {
					this.trigger('request:read', this, xhr, opts);
				},
			);

		return this._getCategoriesById(opts).then((response) => {
			this.ids = [];
			!opts.silent && this.trigger('sync', this, response, opts);
		});
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	getDashboardRecentCategories(opts = {}) {
		if (!opts.silent) {
			this.listenToOnce(
				this,
				'request:_getDashboardRecentCategories',
				(collection, xhr, opts) => {
					this.trigger('request:read', this, xhr, opts);
				},
			);
		}
		return this._getDashboardRecentCategories(opts).then((response) => {
			!opts.silent && this.trigger('sync', this, response, opts);
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'Categories';
	}
}
